// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Badge from "@insite/mobius/Badge";
import GridItem from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel, { SwiperStyle } from "@pace/App/Components/XngCarousel";
import xUseWidgetBadges, { previouslyPurchasedTagId } from "@pace/App/Hooks/xUseWidgetBadges";
import Tooltip from "@pace/MobiusOverrides/Tooltip";

import React, { FC } from "react";

export interface ProductBadgesCarouselStyles {
    wrapper?: InjectableCss;
    imageItem?: InjectableCss;
    textBadgeContainer?: InjectableCss;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    swiper?: SwiperStyle;
    textBadge?: TypographyPresentationProps;
}

export const MAX_NUM_OF_PRODUCT_BADGES = 6;

export const productBadgesCarouselStyles: ProductBadgesCarouselStyles = {};

const styles = productBadgesCarouselStyles;

type Props = WidgetProps & HasProductContext;

const ProductBadgesCarousel: FC<Props> = ({ productContext }) => {
    const { imageBadges = [], textBadges = [] } = xUseWidgetBadges({
        productContext,
        showImageBadges: true,
        showTextBadges: true,
    });
    const previouslyPurchased = textBadges.find(({ id }) => id === previouslyPurchasedTagId);
    if (!imageBadges.length && !previouslyPurchased) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productImageBadgesCarousel">
            {!!imageBadges.length && (
                <XngCarousel
                    navigation={imageBadges.length > MAX_NUM_OF_PRODUCT_BADGES}
                    pagination={false}
                    centerInsufficientSlides={false}
                    slidesOffsetBefore={0}
                    extendedStyles={styles.swiper}
                    slidesPerView={MAX_NUM_OF_PRODUCT_BADGES}
                    spaceBetween={4}
                >
                    {imageBadges.map(badge => (
                        <GridItem key={badge.id} {...styles.imageItem}>
                            <Tooltip
                                text={badge.imageAltText ? badge.imageAltText : badge.name}
                                triggerComponent={
                                    <Badge
                                        type={badge.badgeType}
                                        value={badge.largeImageBadgePath}
                                        imageAltText={badge.imageAltText ? badge.imageAltText : badge.name}
                                        imageBadgeProps={styles.badgeImageProps}
                                    />
                                }
                            />
                        </GridItem>
                    ))}
                </XngCarousel>
            )}
            {previouslyPurchased && (
                <StyledWrapper {...styles.textBadgeContainer}>
                    <Badge
                        type={previouslyPurchased.badgeType}
                        value={previouslyPurchased.displayText}
                        badgeBackgroundColor={previouslyPurchased.badgeColorHexCode}
                        badgeTextColor={previouslyPurchased.textColorHexCode}
                        textBadgeCss={styles.textBadge}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(ProductBadgesCarousel),
    definition: {
        group: "Products",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
